import { useEffect, useRef } from "react";

const eventBus = new EventTarget();
const logInEvent = "logged-in-from-dialog";
const registerEvent = "signed-up-from-dialog";
/**
 * Subscribe to login/register events emitted from the auth dialog. The
 * callback is invoked with `false` after a login and `true` after a new
 * registration.
 *
 * The latest callback ref is held in a ref so listeners can stay stable
 * across renders while still invoking up-to-date component state — callers
 * don't need to memoize or pass a dep array.
 */
export const useOnLogin = (callback: (newUser: boolean) => void) => {
    const callbackRef = useRef(callback);
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    useEffect(() => {
        /*
         * Stable handler refs — addEventListener and removeEventListener must
         * see the same function reference for cleanup to match. The previous
         * version of this hook paired addEventListener with anonymous arrow
         * functions that removeEventListener could never match (and the
         * cleanup path had a typo calling addEventListener again), leaking
         * 3 listeners per mount/unmount.
         */
        const logInHandler = () => callbackRef.current(false);
        const registerHandler = () => callbackRef.current(true);
        eventBus.addEventListener(logInEvent, logInHandler);
        eventBus.addEventListener(registerEvent, registerHandler);
        return () => {
            eventBus.removeEventListener(logInEvent, logInHandler);
            eventBus.removeEventListener(registerEvent, registerHandler);
        };
    }, []);
};


export const emitLoginEvent = () => {
    eventBus.dispatchEvent(new Event(logInEvent));
};

export const emitRegisterEvent = () => {
    eventBus.dispatchEvent(new Event(registerEvent));
};
